import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { BookOpen, MessageSquare, GraduationCap, Trophy, Sparkles, Check } from "lucide-react";

const FEATURES = [
  { icon: BookOpen, title: "Yapay Zeka Konu Anlatımı", desc: "KKTC Kolej Sınavı müfredatındaki konuları Nokta Öğretmen ders ders, çocuk diliyle anlatır.", color: "bg-blue-500" },
  { icon: MessageSquare, title: "Soru-Cevap", desc: "Anlamadığın her noktayı sor, anında örneklerle açıklanan cevaplar al.", color: "bg-rose-500" },
  { icon: GraduationCap, title: "Deneme Sınavları", desc: "Gerçek çıkmış sorular ve AI benzer sorularla sınav provası yap, yanlışlarını öğren.", color: "bg-emerald-500" },
  { icon: Trophy, title: "İlerleme Takibi", desc: "Tamamladığın konular, deneme sonuçların ve başarı grafiklerin tek ekranda.", color: "bg-amber-500" },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const go = () => navigate(user ? "/panel" : "/giris");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo192.png" alt="NoktaDers" className="w-10 h-10 rounded-xl" />
            <span className="font-display text-xl font-bold text-indigo-700">NoktaDers</span>
          </div>
          <Button data-testid="header-login-btn" onClick={go} className="bg-indigo-600 hover:bg-indigo-700 rounded-xl font-bold">
            {user ? "Panelim" : "Giriş Yap"}
          </Button>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-12 grid lg:grid-cols-2 gap-10 items-center">
        <div className="animate-float-up">
          <span className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1.5 rounded-full">
            <Sparkles className="w-4 h-4" /> KKTC KOLEJ SINAVI HAZIRLIK
          </span>
          <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            5. sınıfların <span className="text-indigo-600">yapay zeka</span> öğretmeni! 🦉
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-lg">
            NoktaDers ile KKTC Kolej Sınavı'na eğlenerek hazırlan. Konuları öğren, aklına takılanı sor, deneme sınavlarıyla kendini dene.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button data-testid="hero-start-btn" onClick={go} className="bg-indigo-600 hover:bg-indigo-700 rounded-xl text-base font-bold px-6 py-6">
              Hemen Başla 🚀
            </Button>
            <Button variant="outline" onClick={() => navigate("/giris")} className="rounded-xl text-base font-bold px-6 py-6 border-2">
              Ücretsiz Dene
            </Button>
          </div>
          <div className="mt-6 flex items-center gap-4 text-sm text-slate-500">
            <span className="flex items-center gap-1"><Check className="w-4 h-4 text-emerald-500" /> Türkçe</span>
            <span className="flex items-center gap-1"><Check className="w-4 h-4 text-emerald-500" /> Çocuk dostu</span>
            <span className="flex items-center gap-1"><Check className="w-4 h-4 text-emerald-500" /> Telefona eklenebilir</span>
          </div>
        </div>
        <div className="relative animate-pop-in">
          <div className="absolute -inset-4 bg-gradient-to-tr from-indigo-200 to-sky-200 rounded-[2.5rem] blur-2xl opacity-60" />
          <img
            src="https://images.unsplash.com/photo-1525829528215-ffae12a76ac8?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
            alt="Öğrenciler"
            className="relative rounded-[2rem] shadow-2xl w-full object-cover aspect-[4/3]"
          />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-center text-slate-900">Neler yapabilirsin?</h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map((f, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow animate-float-up" style={{ animationDelay: `${i * 80}ms` }}>
              <div className={`${f.color} w-12 h-12 rounded-2xl flex items-center justify-center text-white mb-4`}>
                <f.icon className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-lg text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-12 text-center">
        <div className="bg-indigo-600 rounded-3xl p-8 sm:p-12 text-white">
          <h2 className="font-display text-2xl sm:text-3xl font-bold">Başarıya bir nokta kaldı! 🎯</h2>
          <p className="mt-3 text-indigo-100">Premium ile sınırsız konu anlatımı, soru-cevap ve tüm deneme sınavları.</p>
          <p className="mt-4 text-3xl font-extrabold">2.750 ₺ <span className="text-lg font-medium text-indigo-200">/ ay</span></p>
          <p className="text-sm text-indigo-200 font-semibold">KDV Dahil</p>
          <Button data-testid="cta-start-btn" onClick={go} className="mt-6 bg-white text-indigo-700 hover:bg-indigo-50 rounded-xl font-bold px-8 py-6 text-base">
            Ücretsiz Hesap Oluştur
          </Button>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8 text-center text-sm text-slate-500">
        <p>© 2026 NoktaDers · KKTC Kolej Sınavı Hazırlık Platformu</p>
      </footer>
    </div>
  );
}
